import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Footer } from "../components/PublicLayout";
import { Mic, Users, MessageCircle, HandHelping, ArrowRight, CheckCircle2 } from "lucide-react";

const Landing = () => {
    return (
        <div className="min-h-screen bg-bg text-ink font-body">
            {/* Top bar */}
            <header className="absolute top-0 left-0 right-0 z-30">
                <div className="container-app py-5 flex items-center justify-between">
                    <Logo />
                    <div className="flex items-center gap-2">
                        <Link to="/login" data-testid="landing-login" className="btn-ghost text-sm">Accedi</Link>
                        <Link to="/register" data-testid="landing-register" className="btn-primary text-sm">Iscriviti</Link>
                    </div>
                </div>
            </header>

            {/* Hero with Arch */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
                    <div className="fade-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface border border-border-line text-xs tracking-widest uppercase text-muted">
                            <Mic size={14} className="text-bordeaux" />
                            Il network del podcast Voci di Imprese
                        </div>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 text-ink">
                            La rete di chi <em className="italic text-bordeaux">costruisce</em> imprese,
                            <span className="block">una voce alla volta.</span>
                        </h1>
                        <p className="mt-6 text-lg text-muted max-w-xl leading-relaxed">
                            Connetti il tuo nome a una comunità di liberi professionisti e imprenditori italiani. Ascolta chi c'è dietro l'impresa, pubblica, collabora e cresci.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/register" data-testid="hero-cta" className="btn-primary text-base">
                                Entra nel network — l'accesso è gratuito
                                <ArrowRight size={18} />
                            </Link>
                            <Link to="/login" data-testid="hero-login" className="btn-outline text-sm">Hai già un account? Accedi</Link>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted">
                            <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-bordeaux" /> Iscrizione gratuita</div>
                            <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-bordeaux" /> Community italiana</div>
                            <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-bordeaux" /> Accesso completo</div>
                        </div>
                    </div>

                    {/* Hero image inside arch frame */}
                    <div className="relative fade-up" style={{ animationDelay: '120ms' }}>
                        <div className="relative arch-top overflow-hidden soft-shadow-lg" style={{ aspectRatio: '4/5' }}>
                            <img
                                src="/foto-home.png"
                                alt="Set podcast Voci di Imprese"
                                className="w-full h-full object-cover"
                                style={{ objectPosition: '50% 78%' }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 text-white">
                                <div className="font-heading italic text-2xl">"Ogni voce è un'impresa."</div>
                                <div className="text-xs tracking-widest mt-2 opacity-80 uppercase">— Voci di Imprese</div>
                            </div>
                        </div>
                        {/* Bordeaux accent ring */}
                        <div className="absolute -inset-3 arch-top border border-bordeaux/30 -z-10" />
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-20 bg-surface/40 border-y border-border-line">
                <div className="container-app">
                    <div className="max-w-2xl">
                        <div className="text-xs tracking-[0.3em] uppercase text-soft">Cosa puoi fare</div>
                        <h2 className="font-heading text-4xl md:text-5xl mt-3 leading-tight text-ink">Più di un social.<br />Un salotto professionale.</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                        {[
                            { icon: Users, title: "Profili professionali", desc: "Mostra chi sei: competenze, servizi, esperienze, interviste collegate." },
                            { icon: MessageCircle, title: "Conversazioni dirette", desc: "Chat 1:1 in tempo reale con altri membri della rete." },
                            { icon: HandHelping, title: "Chiedi aiuto", desc: "Hai bisogno di una mano? Chiedi alla rete e ricevi risposte." },
                            { icon: Mic, title: "Le interviste", desc: "Ascolta chi c'è dietro l'impresa. Capire prima di affidarsi." },
                        ].map((f, i) => (
                            <div key={i} className="bg-paper rounded-2xl p-6 soft-shadow border border-border-line hover:-translate-y-1 transition-transform duration-300">
                                <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center mb-4">
                                    <f.icon size={20} className="text-bordeaux" />
                                </div>
                                <h3 className="font-heading text-xl mb-2 text-ink">{f.title}</h3>
                                <p className="text-sm text-muted leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Join CTA card */}
            <section className="py-24">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <div className="text-xs tracking-[0.3em] uppercase text-soft">Iscriviti</div>
                    <h2 className="font-heading text-4xl md:text-5xl mt-3 text-ink">La rete di chi costruisce imprese,<br />una voce alla volta.</h2>
                    <div className="mt-10 rounded-2xl bg-paper border border-border-line soft-shadow-lg p-10 relative">
                        <div className="font-heading text-5xl text-ink">L'accesso è <em className="italic text-bordeaux">gratuito.</em></div>
                        <p className="mt-4 text-muted max-w-md mx-auto">
                            Iscriviti ed esplora il network senza vincoli, quando vuoi.
                        </p>
                        <div className="mt-8 flex justify-center">
                            <Link to="/register" data-testid="pricing-cta" className="btn-primary text-base">
                                Iscriviti gratis — entra nel network
                                <ArrowRight size={18} />
                            </Link>
                        </div>
                        <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm text-left max-w-md mx-auto">
                            {["Profilo professionale", "Feed completo", "Chat illimitate", "Accesso interviste", "Bacheca richieste", "Tutto incluso, gratis"].map((x) => (
                                <li key={x} className="flex items-center gap-2 text-ink"><CheckCircle2 size={16} className="text-bordeaux" />{x}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Landing;
